import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, Link } from 'react-router-dom';
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher';
import { legalDocumentPathForUiLocale, normalizeUiLocale } from '../legal/documents';

interface PublicLayoutProps {
  /**
   * The marketing hero is shown on every public page by default. Legal
   * documents render their own `h1` and start their body at `h2`, so the hero
   * (an `h2` above the page heading) would break heading order — legal routes
   * mount this layout with `showHero={false}`.
   */
  showHero?: boolean;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({ showHero = true }) => {
  const { t, i18n } = useTranslation();
  const isAuthenticated = !!localStorage.getItem('token');
  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/">
            <h1 className="text-2xl text-foreground cursor-pointer">{t('app.title')}</h1>
          </Link>
          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="text-sm font-medium text-foreground hover:text-muted-foreground flex items-center"
                title={t('landing.header.dashboard')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
                <span className="ml-1.5">{t('landing.header.dashboard')}</span>
              </Link>
            ) : (
              <Link to="/login" className="text-sm font-medium text-foreground hover:text-muted-foreground">{t('auth.login.submit')}</Link>
            )}
          </div>
        </div>
      </header>

      {showHero && (
        <section className="text-center pt-12">
          <h2 className="text-3xl text-foreground mb-3">{t('landing.hero.headline')}</h2>
          <p className="text-lg text-muted-foreground">{t('landing.hero.subtitle')}</p>
        </section>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      <footer className="bg-card border-t border-border mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav
            aria-label={t('legal.documents_nav')}
            className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-4"
          >
            <a
              href={legalDocumentPathForUiLocale('privacy', uiLocale)}
              className="text-sm text-foreground underline underline-offset-2 hover:text-muted-foreground"
            >
              {t('legal.privacy')}
            </a>
            <a
              href={legalDocumentPathForUiLocale('terms', uiLocale)}
              className="text-sm text-foreground underline underline-offset-2 hover:text-muted-foreground"
            >
              {t('legal.terms')}
            </a>
          </nav>
          <p className="text-center text-muted-foreground text-sm">
            {t('app.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};